import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Share2, Copy, Link2Off, MessageSquare, Trash2, X, RefreshCw } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { Button } from "../ui/button";

const fmt = (d) => (d ? new Date(d).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "");

export function SharePanel({ workId, onClose }) {
  const [token, setToken] = useState("");
  const [comments, setComments] = useState([]);
  const link = token ? `${window.location.origin}/cv/partage/${token}` : "";

  const load = async () => {
    if (!workId) return;
    try { const { data } = await api.get(`/cv/works/${workId}/comments`); setToken(data.token || ""); setComments(data.comments || []); } catch { /* */ }
  };
  useEffect(() => { load(); }, [workId]); // eslint-disable-line

  const create = async () => { try { const { data } = await api.post(`/cv/works/${workId}/share`); setToken(data.token); toast.success("Lien de partage créé"); } catch (e) { toast.error(apiError(e, "Partage impossible")); } };
  const revoke = async () => { try { await api.delete(`/cv/works/${workId}/share`); setToken(""); toast.success("Lien révoqué"); } catch (e) { toast.error(apiError(e)); } };
  const copy = async () => { try { await navigator.clipboard.writeText(link); toast.success("Lien copié"); } catch { toast.error("Copie impossible — sélectionnez le lien"); } };
  const delComment = async (id) => { try { await api.delete(`/cv/works/${workId}/comments/${id}`); setComments((c) => c.filter((x) => x.id !== id)); } catch { /* */ } };

  return (
    <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-5" data-testid="cv-share">
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-2 font-semibold text-navy"><Share2 size={16} className="text-[#0E7C7B]" /> Partage avec un relecteur</p>
        <button onClick={onClose} className="text-slate-400 hover:text-navy" data-testid="cv-share-close"><X size={18} /></button>
      </div>
      {!workId ? <p className="mt-3 text-sm text-slate-400">Enregistrez d'abord le document.</p> : (
        <>
          <p className="mt-1 text-xs text-slate-400">Le relecteur (mentor, collègue) ouvre le lien sans compte, lit le document et laisse des commentaires. Le lien peut être révoqué à tout moment.</p>
          {token ? (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <input readOnly value={link} onFocus={(e) => e.target.select()} data-testid="cv-share-link" className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-xs text-navy" />
              <Button onClick={copy} data-testid="cv-share-copy" className="rounded-full bg-[#0E7C7B] text-xs text-white hover:bg-[#0c6a69]"><Copy size={13} className="mr-1" /> Copier</Button>
              <Button onClick={revoke} data-testid="cv-share-revoke" variant="outline" className="rounded-full border-red-300 text-xs text-red-600 hover:bg-red-50"><Link2Off size={13} className="mr-1" /> Révoquer</Button>
            </div>
          ) : (
            <Button onClick={create} data-testid="cv-share-create" className="mt-3 rounded-full bg-[#0E7C7B] text-xs text-white hover:bg-[#0c6a69]"><Share2 size={13} className="mr-1" /> Créer un lien de partage</Button>
          )}
          <div className="mt-4 flex items-center justify-between">
            <p className="flex items-center gap-2 text-sm font-semibold text-navy"><MessageSquare size={15} /> Commentaires des relecteurs ({comments.length})</p>
            <button onClick={load} className="text-xs text-slate-500 hover:text-navy" data-testid="cv-share-refresh"><RefreshCw size={13} className="mr-1 inline" /> Actualiser</button>
          </div>
          {comments.length === 0 ? <p className="mt-2 text-xs text-slate-400">Aucun commentaire pour l'instant.</p> : (
            <ul className="mt-2 space-y-2" data-testid="cv-share-comments">
              {comments.map((c) => (
                <li key={c.id} className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs" data-testid={`cv-comment-${c.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-navy">{c.author} <span className="font-normal text-slate-400">· {fmt(c.created_at)}</span></span>
                    <button onClick={() => delComment(c.id)} className="text-slate-400 hover:text-red-500" data-testid={`cv-comment-del-${c.id}`}><Trash2 size={13} /></button>
                  </div>
                  {c.anchor && <p className="mt-1 border-l-2 border-[#0E7C7B] pl-2 italic text-slate-500">« {c.anchor} »</p>}
                  <p className="mt-1 whitespace-pre-wrap text-slate-700">{c.text}</p>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
